import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { printRegistre } from '../utils/printDocument';

const C = {
  bg: '#12141f', card: '#1a1d2e', border: '#2a2d3e',
  text: '#e2e8f0', muted: '#94a3b8', faint: '#64748b',
  accent: '#41B073', green: '#22c55e', red: '#ef4444',
  orange: '#f97316', yellow: '#eab308',
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const Badge = ({ text, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>
);

function ScoreGauge({ score }) {
  const color = score >= 80 ? C.green : score >= 50 ? C.yellow : C.red;
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 20 }}>
      <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
        <svg viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="40" cy="40" r="30" fill="none" stroke={C.border} strokeWidth="8" />
          <circle cx="40" cy="40" r="30" fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${2 * Math.PI * 30}`}
            strokeDashoffset={`${2 * Math.PI * 30 * (1 - score / 100)}`}
            strokeLinecap="round" />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color, fontFamily: 'DM Mono, monospace' }}>{score}%</div>
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Score de conformité global</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
          {score >= 80 ? '✅ Bonne conformité RGPD' : score >= 50 ? '⚠️ Conformité partielle — actions requises' : '🔴 Conformité insuffisante — action urgente'}
        </div>
        <div style={{ fontSize: 11, color: C.faint, marginTop: 6 }}>Calculé sur : DPA signé · Base légale renseignée · Sensibilité renseignée</div>
      </div>
    </div>
  );
}

function KPIBox({ icon, title, value, sub, color, alert }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${alert ? color + '55' : C.border}`, borderRadius: 10, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
      {alert && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color }} />}
      <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: 'DM Mono, monospace', marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ConformitePage() {
  const fetchWithAuth = useAuthFetch();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRegistre, setLoadingRegistre] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchWithAuth('/api/rgpd/conformite').then(r => r.json());
      setData(d);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmerSuppression = async (id) => {
    setSavingId(id);
    await fetchWithAuth(`/api/missions/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ rgpd_statut: 'supprime' }) });
    setSavingId(null); load();
  };

  const exportRegistre = async () => {
    setLoadingRegistre(true);
    try {
      const d = await fetchWithAuth('/api/rgpd/registre').then(r => r.json());
      printRegistre(d);
    } finally { setLoadingRegistre(false); }
  };

  if (loading) return <div style={{ padding: 28, color: C.faint }}>Chargement…</div>;
  if (!data)   return <div style={{ padding: 28, color: C.red }}>Erreur de chargement</div>;

  const totalConformite = data.dpa_signes + data.dpa_non_signes;
  const pcDpa = totalConformite > 0 ? Math.round(data.dpa_signes / totalConformite * 100) : 0;

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Tableau de bord conformité RGPD</h1>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>Vue d'ensemble des obligations légales de traitement des données</div>
        </div>
        <button onClick={exportRegistre} disabled={loadingRegistre}
          style={{ padding: '9px 18px', borderRadius: 8, background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
          {loadingRegistre ? 'Génération…' : '📋 Exporter Registre CNIL'}
        </button>
      </div>

      {/* Score + KPIs */}
      <div style={{ marginBottom: 24 }}>
        <ScoreGauge score={data.score_conformite} />
      </div>

      <div className="kpi-grid" style={{ marginBottom: 28 }}>
        <KPIBox icon="✅" title="DPA signés" value={data.dpa_signes} sub={`${pcDpa}% des clients`} color={C.green} />
        <KPIBox icon="⚠️" title="Sans DPA" value={data.dpa_non_signes} sub="Action recommandée" color={C.orange} alert={data.dpa_non_signes > 0} />
        <KPIBox icon="🔴" title="Suppression J+30" value={data.a_supprimer_urgence} sub="Action immédiate requise" color={C.red} alert={data.a_supprimer_urgence > 0} />
        <KPIBox icon="⚠️" title="Suppression dans 5j" value={data.a_supprimer_bientot} sub="À traiter cette semaine" color={C.orange} alert={data.a_supprimer_bientot > 0} />
      </div>

      {/* Alertes J+30 */}
      {data.missions_urgentes?.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.red}44`, borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 18 }}>🔴</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.red }}>Suppressions requises — J+30 dépassé</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.missions_urgentes.map(m => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.bg, borderRadius: 8, padding: '10px 14px', borderLeft: `3px solid ${C.red}` }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{m.client_nom}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{m.titre} · {fmtDate(m.date_fin)} · <span style={{ color: C.red, fontWeight: 700 }}>J+{m.days_since_end}</span></div>
                </div>
                <button disabled={savingId === m.id} onClick={() => confirmerSuppression(m.id)}
                  style={{ padding: '7px 14px', borderRadius: 8, background: C.red, border: 'none', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                  {savingId === m.id ? '…' : '✓ Confirmer suppression'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alertes J+25 */}
      {data.missions_bientot?.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.orange}44`, borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.orange }}>Suppressions imminentes — dans 5 jours</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.missions_bientot.map(m => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.bg, borderRadius: 8, padding: '10px 14px', borderLeft: `3px solid ${C.orange}` }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{m.client_nom}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{m.titre} · Date limite : <span style={{ color: C.orange, fontWeight: 700 }}>{fmtDate(m.rgpd_date_suppression)}</span></div>
                </div>
                <Badge text={`J+${m.days_since_end}`} color={C.orange} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clients sans DPA */}
      {data.dpa_non_signes > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 18 }}>📋</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Clients sans DPA signé</span>
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginBottom: 12 }}>
            {data.dpa_non_signes} client{data.dpa_non_signes > 1 ? 's' : ''} sans accord de traitement de données signé.
            Rendez-vous dans la fiche client (onglet RGPD) pour saisir la date de signature et générer le document.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigate('/clients')} style={{ padding: '7px 14px', borderRadius: 8, background: C.accent + '22', border: `1px solid ${C.accent}44`, color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
              👥 Voir les clients
            </button>
          </div>
        </div>
      )}

      {/* Clients critiques/sensibles */}
      {(data.clients_critiques > 0 || data.clients_sensibles > 0) && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 12 }}>🔒 Clients à données sensibles</div>
          <div style={{ display: 'flex', gap: 16 }}>
            {data.clients_critiques > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.bg, borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ fontSize: 20 }}>🚨</span>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.red, fontFamily: 'DM Mono, monospace' }}>{data.clients_critiques}</div>
                  <div style={{ fontSize: 11, color: C.faint }}>Critique</div>
                </div>
              </div>
            )}
            {data.clients_sensibles > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.bg, borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.orange, fontFamily: 'DM Mono, monospace' }}>{data.clients_sensibles}</div>
                  <div style={{ fontSize: 11, color: C.faint }}>Sensible</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Aucune alerte */}
      {data.a_supprimer_urgence === 0 && data.a_supprimer_bientot === 0 && data.dpa_non_signes === 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.green}44`, borderRadius: 10, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.green }}>Aucune alerte RGPD active</div>
          <div style={{ fontSize: 13, color: C.faint, marginTop: 4 }}>Toutes les obligations de traitement sont à jour.</div>
        </div>
      )}
    </div>
  );
}
